import React, { useContext, useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import { ExamContext, MeContext, userContext } from "../../App";
import axios from "../../Utils/Axios.config";
import { format } from "date-fns";
import { ProgressBar } from "react-loader-spinner";
import {
  classFiveSub,
  jscSub,
  primarySub,
  secondarySub,
  sscSub,
} from "../../data/subjectData";
import { GoodFeedback, badFeedback, normalFeedback } from "../../data/feedback";
import { allClassData } from "../../data/classData";
import ExcelUploadModal from "./ExcelUploadModal";

const BulkResultEntry = () => {
  const [me, meLoading] = useContext(MeContext);
  const [xms, xmLoading, xmRefetch, myXm, xmError] = useContext(ExamContext);
  const [users, userLoading, userRefetch, userError] = useContext(userContext);
  
  const students = users?.filter((i) => i?.role?.includes("student"));
  const [date, setDate] = useState(new Date());
  const [feedback, setFeedback] = useState(normalFeedback);
  const [disable, setDisable] = useState(false);
  const [sub, setSub] = useState(primarySub);
  const [classes, setClass] = useState("Play");
  const [subject, setSubject] = useState(sub[0]?.name);
  const [fullClass, setFullClass] = useState("Play");
  const [data, setData] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [resultData, setResultData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [savingStudents, setSavingStudents] = useState(new Set());
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [excelData, setExcelData] = useState([]);

  const formateDate = format(date, "PP");

  // Get current exam
  const Myxm = xms?.find((i) => i?.status?.includes("processing"));

  // Clear input fields when subject changes
  useEffect(() => {
    if (data.length > 0) {
      // Clear all input states when subject changes
      setResultData({});
      setEditMode({});
      setBulkEditMode(false);
      
      // Re-initialize result data for current students
      initializeResultData(data);
    }
  }, [subject]);

  // Class selection handler
  const handleClass = (classData) => {
    setFullClass(classData);
    let filteredStudents = [];

    if (
      classData === "One-Boys" ||
      classData === "One-Girls" ||
      classData === "Two-Boys" ||
      classData === "Two-Girls" ||
      classData === "Three-Boys" ||
      classData === "Three-Girls" ||
      classData === "Four-Boys" ||
      classData === "Four-Girls" ||
      classData === "Five-Boys" ||
      classData === "Five-Girls" ||
      classData === "Six-Boys" ||
      classData === "Six-Girls" ||
      classData === "Seven-Boys" ||
      classData === "Seven-Girls" ||
      classData === "Eight-Boys" ||
      classData === "Eight-Girls" ||
      classData === "Nine-Boys" ||
      classData === "Nine-Girls" ||
      classData === "Ten-Boys" ||
      classData === "Ten-Girls"
    ) {
      const studentValue = students?.filter(
        (i) => i?.class === classData?.split("-")[0]
      );

      const st = studentValue?.filter((i) => {
        if (classData?.split("-")[1] === "Boys") {
          return i?.gender === "male";
        } else {
          return i?.gender === "female";
        }
      });
      filteredStudents = st?.sort(function (a, b) {
        return a.roleNo - b.roleNo;
      });
      setClass(classData?.split("-")[0]);
    } else {
      const studentValue = students?.filter((i) =>
        i?.class?.includes(classData)
      );
      filteredStudents = studentValue?.sort(function (a, b) {
        return a.roleNo - b.roleNo;
      });
      setClass(classData);
    }

    setData(filteredStudents || []);
    initializeResultData(filteredStudents || []);

    // Clear all input states when class changes
    setResultData({});
    setEditMode({});
    setBulkEditMode(false);

    // Subject selection based on class
    if (
      classData === "Play" ||
      classData === "Nursery" ||
      classData?.split("-")[0] === "One" ||
      classData?.split("-")[0] === "Two"
    ) {
      setSub(primarySub);
      setSubject(primarySub[0].name);
    } else if (
      classData?.split("-")[0] === "Three" ||
      classData?.split("-")[0] === "Four"
    ) {
      setSub(secondarySub);
      setSubject(secondarySub[0].name);
    } else if (classData?.split("-")[0] === "Five") {
      setSub(classFiveSub);
      setSubject(classFiveSub[0].name);
    } else if (classData === "Six" || classData?.split("-")[0] === "Seven" || classData?.split("-")[0] === "Six") {
      setSub(jscSub);
      setSubject(jscSub[0].name);
    } else if (classData?.split("-")[0] === "Eight") {
      setSub(jscSub);
      setSubject(jscSub[0].name);
    } else {
      setSub(sscSub);
      setSubject(sscSub[0].name);
    }
  };

  // Initialize result data for all students
  const initializeResultData = (students) => {
    const initialData = {};
    students.forEach((student) => {
      initialData[student.phoneNumber] = {
        writing: "",
        mcq: "",
        practical: "",
        feedback: normalFeedback,
      };
    });
    setResultData(initialData);
  };

  // Handle input changes - memoized for performance
  const handleInputChange = useCallback((studentPhone, field, value) => {
    setResultData(prev => ({
      ...prev,
      [studentPhone]: {
        ...prev[studentPhone],
        [field]: value
      }
    }));
  }, []);

  // Handle bulk input change - memoized for performance
  const handleBulkInputChange = useCallback((field, value) => {
    setResultData(prev => {
      const newResultData = { ...prev };
      Object.keys(newResultData).forEach(phone => {
        newResultData[phone] = {
          ...newResultData[phone],
          [field]: value
        };
      });
      return newResultData;
    });
  }, []);

  // Get current subject details - memoized for performance
  const currentSub = useMemo(() => {
    return sub?.find((i) => i?.name?.includes(subject));
  }, [sub, subject]);

  // Check if result already exists for a student - memoized for performance
  const checkExistingResult = useCallback((studentPhone) => {
    if (!Myxm?.Results) return null;
    const existingResult = Myxm.Results.find((i) => i.studentPh === studentPhone);
    if (!existingResult) return null;
    return existingResult.subResult?.find((i) => i.subject === subject);
  }, [Myxm?.Results, subject]);

  // Clean data based on subject requirements before API submission - memoized for performance
  const cleanDataForSubmission = useCallback((studentData, subjectInfo) => {
    const cleanedData = { ...studentData };
    
    // If subject doesn't have MCQ, clear MCQ data
    if (!subjectInfo?.mcq) {
      cleanedData.mcq = "";
    }
    
    // If subject doesn't have practical, clear practical data
    if (!subjectInfo?.practical) {
      cleanedData.practical = "";
    }
    
    // If subject doesn't have writing, clear writing data
    if (!subjectInfo?.wMax) {
      cleanedData.writing = "";
    }
    
    return cleanedData;
  }, []);

  // Validation function for input values - memoized for performance
  const validateInput = useCallback((value, maxValue, fieldName) => {
    if (value === '' || value === null || value === undefined) return true;
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return false;
    if (numValue < 0) return false;
    if (maxValue && numValue > maxValue) return false;
    return true;
  }, []);

  // Get validation error message - memoized for performance
  const getValidationError = useCallback((value, maxValue, fieldName) => {
    if (value === '' || value === null || value === undefined) return '';
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return `${fieldName} must be a valid number`;
    if (numValue < 0) return `${fieldName} cannot be negative`;
    if (maxValue && numValue > maxValue) return `${fieldName} cannot exceed ${maxValue}`;
    return '';
  }, []);

  // Save individual result
  const saveIndividualResult = async (student) => {
    const studentData = resultData[student.phoneNumber];
    if (!studentData) return;

    // Prevent duplicate submission
    if (savingStudents.has(student.phoneNumber)) {
      toast.warning(`⚠️ ${student.firstName}: Already saving result`);
      return;
    }

    // Add to saving set
    setSavingStudents(prev => new Set(prev).add(student.phoneNumber));

    // Check if result already exists
    const existingResult = checkExistingResult(student.phoneNumber);

    // Clean data based on subject requirements before processing
    const cleanedData = cleanDataForSubmission(studentData, currentSub);

    // Validate inputs before saving
    const writing = cleanedData.writing ? parseFloat(cleanedData.writing) : 0;
    const mcq = cleanedData.mcq ? parseFloat(cleanedData.mcq) : 0;
    const practical = cleanedData.practical ? parseFloat(cleanedData.practical) : 0;

    // Check if at least writing or MCQ is provided
    const hasWriting = currentSub?.wMax && writing > 0;
    const hasMcq = currentSub?.mcq && mcq > 0;
    const hasPractical = currentSub?.practical && practical > 0;

    if (!hasWriting && !hasMcq && !hasPractical) {
      toast.warning(`⚠️ ${student.firstName}: Please provide at least writing or MCQ marks`);
      setSavingStudents(prev => {
        const newSet = new Set(prev);
        newSet.delete(student.phoneNumber);
        return newSet;
      });
      return;
    }

    // Collect all validation errors for this student
    const studentErrors = [];
    if (currentSub?.wMax && writing > currentSub.wMax) {
      studentErrors.push(`Writing: max ${currentSub.wMax}`);
    }
    if (currentSub?.mcq && mcq > currentSub.mcq) {
      studentErrors.push(`MCQ: max ${currentSub.mcq}`);
    }
    if (currentSub?.practical && practical > currentSub.practical) {
      studentErrors.push(`Practical: max ${currentSub.practical}`);
    }

    if (studentErrors.length > 0) {
      const errorMessage = `❌ ${student.firstName}: ${studentErrors.join(', ')}`;
      toast.error(errorMessage);
      setSavingStudents(prev => {
        const newSet = new Set(prev);
        newSet.delete(student.phoneNumber);
        return newSet;
      });
      return;
    }

    try {
      if (existingResult) {
        // Update existing result - preserve existing values if not being updated
        const subResult = {
          studentPh: student.phoneNumber,
          subject: subject,
          writing: writing > 0 ? writing : (existingResult.writing || undefined),
          mcq: mcq > 0 ? mcq : (existingResult.mcq || undefined),
          pecticNun: practical > 0 ? practical : (existingResult.pecticNun || undefined),
          feedback: cleanedData.feedback,
        };
        await axios.patch(`/exam/editResult/${Myxm?._id}`, subResult);
        toast.success(`✅ ${student.firstName}: Result updated successfully`);
      } else {
        // Add new result
        const result = {
          studentPh: student.phoneNumber,
          class: student.class,
          roleNo: student.roleNo,
          subResult: [
            {
              subject: subject,
              writing: writing > 0 ? writing : undefined,
              mcq: mcq > 0 ? mcq : undefined,
              pecticNun: practical > 0 ? practical : undefined,
              sir: me?.firstName + " " + me?.lastName,
              feedback: cleanedData.feedback,
              date: date,
            },
          ],
        };

        await axios.patch(`/exam/addResult/${Myxm?._id}`, result);
        toast.success(`✅ ${student.firstName}: Result saved successfully`);
      }
      
      // Clear input state for this student after successful submission
      setResultData(prev => ({
        ...prev,
        [student.phoneNumber]: {
          writing: "",
          mcq: "",
          practical: "",
          feedback: normalFeedback,
        }
      }));
      
      // Exit edit mode for this student
      setEditMode(prev => ({
        ...prev,
        [student.phoneNumber]: false
      }));
      
      xmRefetch();
    } catch (error) {
      console.error(error);
      toast.error(`❌ ${student.firstName}: Failed to save result`);
    } finally {
      // Remove from saving set
      setSavingStudents(prev => {
        const newSet = new Set(prev);
        newSet.delete(student.phoneNumber);
        return newSet;
      });
    }
  };

  // Save all results
  const saveAllResults = async () => {
    if (isLoading) {
      toast.warning("⚠️ Already saving results, please wait...");
      return;
    }
    
    setIsLoading(true);
    setDisable(true);

    try {
      // Validate all inputs before saving
      const validationErrors = [];
      const studentsToSkip = [];
      
      data.forEach(student => {
        const studentData = resultData[student.phoneNumber];
        if (!studentData) {
          studentsToSkip.push(student.phoneNumber);
          return;
        }

        // Clean data based on subject requirements before processing
        const cleanedData = cleanDataForSubmission(studentData, currentSub);

        const writing = cleanedData.writing ? parseFloat(cleanedData.writing) : 0;
        const mcq = cleanedData.mcq ? parseFloat(cleanedData.mcq) : 0;
        const practical = cleanedData.practical ? parseFloat(cleanedData.practical) : 0;

        // Check if at least writing or MCQ is provided
        const hasWriting = currentSub?.wMax && writing > 0;
        const hasMcq = currentSub?.mcq && mcq > 0;
        const hasPractical = currentSub?.practical && practical > 0;

        if (!hasWriting && !hasMcq && !hasPractical) {
          studentsToSkip.push(student.phoneNumber);
          validationErrors.push(`${student.firstName}: No marks provided (skipped)`);
          return;
        }

        if (currentSub?.wMax && writing > currentSub.wMax) {
          validationErrors.push(`${student.firstName}: Writing marks cannot exceed ${currentSub.wMax}`);
        }
        if (currentSub?.mcq && mcq > currentSub.mcq) {
          validationErrors.push(`${student.firstName}: MCQ marks cannot exceed ${currentSub.mcq}`);
        }
        if (currentSub?.practical && practical > currentSub.practical) {
          validationErrors.push(`${student.firstName}: Practical marks cannot exceed ${currentSub.practical}`);
        }
      });

      // Separate critical errors from skip warnings
      const skipWarnings = validationErrors.filter(error => error.includes('skipped'));
      const criticalErrors = validationErrors.filter(error => !error.includes('skipped'));

      if (criticalErrors.length > 0) {
        const errorMessage = `Please fix validation errors:\n${criticalErrors.join('\n')}`;
        toast.error(errorMessage);
        setIsLoading(false);
        setDisable(false);
        return;
      }

      // Process each student result (skip students with no marks)
      let savedCount = 0;
      for (const student of data) {
        // Skip students with no marks
        if (studentsToSkip.includes(student.phoneNumber)) {
          continue;
        }

        const studentData = resultData[student.phoneNumber];
        if (!studentData) continue;

        // Clean data based on subject requirements before processing
        const cleanedData = cleanDataForSubmission(studentData, currentSub);

        const existingResult = checkExistingResult(student.phoneNumber);
        const writing = cleanedData?.writing ? parseFloat(cleanedData.writing) : 0;
        const mcq = cleanedData?.mcq ? parseFloat(cleanedData.mcq) : 0;
        const practical = cleanedData?.practical ? parseFloat(cleanedData.practical) : 0;

        if (existingResult) {
          // Update existing result
          const subResult = {
            studentPh: student.phoneNumber,
            subject: subject,
            writing: writing > 0 ? writing : (existingResult.writing || undefined),
            mcq: mcq > 0 ? mcq : (existingResult.mcq || undefined),
            pecticNun: practical > 0 ? practical : (existingResult.pecticNun || undefined),
            feedback: cleanedData?.feedback || normalFeedback,
          };

          await axios.patch(`/exam/editResult/${Myxm?._id}`, subResult);
          savedCount++;
        } else {
          // Add new result
          const result = {
            studentPh: student.phoneNumber,
            class: student.class,
            roleNo: student.roleNo,
            subResult: [
              {
                subject: subject,
                writing: writing > 0 ? writing : undefined,
                mcq: mcq > 0 ? mcq : undefined,
                pecticNun: practical > 0 ? practical : undefined,
                sir: me?.firstName + " " + me?.lastName,
                feedback: cleanedData?.feedback || normalFeedback,
                date: date,
              },
            ],
          };

          await axios.patch(`/exam/addResult/${Myxm?._id}`, result);
          savedCount++;
        }
      }

      // Create comprehensive success message
      let successMessage = '';
      if (savedCount > 0) {
        successMessage = `✅ ${savedCount} results saved successfully!`;
        if (skipWarnings.length > 0) {
          successMessage += `\n⚠️ ${skipWarnings.length} students skipped (no marks provided)`;
        }
        toast.success(successMessage);
        
        // Clear all input states after successful bulk save
        const clearedResultData = {};
        data.forEach(student => {
          clearedResultData[student.phoneNumber] = {
            writing: "",
            mcq: "",
            practical: "",
            feedback: normalFeedback,
          };
        });
        setResultData(clearedResultData);
      } else {
        toast.warning("No results were saved (all students had no marks)");
      }
      xmRefetch();
      setEditMode({});
      setBulkEditMode(false);
    } catch (error) {
      console.error(error);
      toast.error("❌ Failed to save some results. Please try again.");
    } finally {
      setIsLoading(false);
      setDisable(false);
    }
  };

  // Toggle edit mode for individual student - memoized for performance
  const toggleEditMode = useCallback((studentPhone) => {
    setEditMode(prev => ({
      ...prev,
      [studentPhone]: !prev[studentPhone]
    }));
  }, []);

  // Toggle bulk edit mode - memoized for performance
  const toggleBulkEditMode = useCallback(() => {
    setBulkEditMode(prev => {
      if (!prev) {
        // Initialize edit mode for all students
        const editModes = {};
        data.forEach(student => {
          editModes[student.phoneNumber] = true;
        });
        setEditMode(editModes);
      } else {
        setEditMode({});
      }
      return !prev;
    });
  }, [data]);

  // Handle Excel data processing - memoized for performance
  const handleExcelDataProcessed = useCallback((processedData) => {
    setExcelData(processedData);
    
    // Convert Excel data to resultData format
    const newResultData = {};
    processedData.forEach(row => {
      if (row.matchedStudent) {
        newResultData[row.matchedStudent.phoneNumber] = {
          writing: row.writing > 0 ? row.writing.toString() : '',
          mcq: row.mcq > 0 ? row.mcq.toString() : '',
          practical: row.practical > 0 ? row.practical.toString() : '',
          feedback: row.feedback || normalFeedback,
        };
      }
    });

    // Update resultData with Excel data
    setResultData(prev => ({
      ...prev,
      ...newResultData
    }));

    // Enable edit mode for all students with Excel data
    const editModes = {};
    processedData.forEach(row => {
      if (row.matchedStudent) {
        editModes[row.matchedStudent.phoneNumber] = true;
      }
    });
    setEditMode(editModes);
    setBulkEditMode(true);

    // Show success message
    const matchedCount = processedData.filter(row => row.matchedStudent).length;
    const unmatchedCount = processedData.length - matchedCount;
    
    if (matchedCount > 0) {
      toast.success(`✅ Excel data loaded: ${matchedCount} students ready for upload`);
    }
    if (unmatchedCount > 0) {
      toast.warning(`⚠️ ${unmatchedCount} students not found in class`);
    }
  }, [normalFeedback]);

  if (userLoading || meLoading || xmLoading) {
    return <div className="flex justify-center items-center h-64">
      <ProgressBar
        height="80"
        width="80"
        ariaLabel="progress-bar-loading"
        wrapperStyle={{}}
        wrapperClass="progress-bar-wrapper"
        borderColor="#F4442E"
        barColor="#51E5FF"
      />
    </div>;
  }

  return (
    <div className="p-2 sm:p-0">
      <div className="w-full">
        <div className="bg-white rounded-lg w-full p-3">
          <div className="w-full flex justify-between">
            <h1 className="text-xl text-cyan-600 font-bold">
              Bulk Result Entry - {me?.firstName} {me?.lastName}
            </h1>
            <h1 className="text-xl text-purple-700 font-bold">{formateDate}</h1>
          </div>
          
          {/* Class and Subject Selection */}
          <div className="flex w-full mt-3 gap-x-2 sm:gap-x-3 justify-between">
            <div className="w-full">
              <p className="text-green-800 mb-1">Select Class</p>
              <select
                onChange={(e) => handleClass(e.target.value)}
                className="select sm:select-md select-sm select-bordered px-1 select-success rounded-md w-full"
              >
                {allClassData?.map((item, index) => (
                  <option key={index} value={item?.className}>
                    {item?.className}
                  </option>
                ))}
              </select>
            </div>
            <div className="w-full">
              <p className="text-green-800 mb-1">Select Subject</p>
              <select
                onChange={(e) => setSubject(e.target.value)}
                className="select sm:select-md select-sm select-bordered px-1 select-success rounded-md w-full"
              >
                {sub?.map((item, index) => (
                  <option key={index} value={item?.name.toString()}>
                    {item?.name.toString()}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Bulk Edit Controls */}
          {data.length > 0 && (
            <div className="mt-4 flex gap-2 flex-wrap">
              <button
                onClick={toggleBulkEditMode}
                className={`btn btn-sm ${bulkEditMode ? 'btn-error' : 'btn-primary'}`}
              >
                {bulkEditMode ? 'Cancel Bulk Edit' : 'Edit All Students'}
              </button>
              <button
                onClick={() => setShowExcelModal(true)}
                className="btn btn-sm btn-info"
              >
                📊 Bulk Upload Excel
              </button>
              {bulkEditMode && (
                <button
                  onClick={saveAllResults}
                  disabled={disable || isLoading}
                  className={`btn btn-sm btn-success ${isLoading ? 'loading' : ''}`}
                >
                  {isLoading ? 'Saving All...' : 'Save All Results'}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Results Table */}
        {data.length > 0 && (
          <div className="bg-white rounded-lg mt-4 overflow-x-auto">
            <div className="overflow-x-auto">
              <table className="table table-zebra w-full">
                <thead>
                  <tr>
                    <th className="lg:sticky left-0 bg-base-200">Student Info</th>
                    {currentSub?.wMax && <th className="text-center">Writing ({currentSub.wMax})</th>}
                    {currentSub?.mcq && <th className="text-center">MCQ ({currentSub.mcq})</th>}
                    {currentSub?.practical && <th className="text-center">Practical ({currentSub.practical})</th>}
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((student, index) => {
                    const isEditModeActive = editMode[student.phoneNumber] || bulkEditMode;
                    const existingResult = checkExistingResult(student.phoneNumber);
                    const studentData = resultData[student.phoneNumber] || {};

                    return (
                      <tr key={student.phoneNumber} className={existingResult ? 'bg-green-50' : ''}>
                        {/* Student Info & Roll */}
                        <td className="lg:sticky left-0 bg-base-100">
                          <div className="flex items-center gap-2">
                            {student?.imageURL ? (
                              <div className="w-8 h-8 hidden lg:block ring ring-[#91f2dc] ring-offset-base-100 ring-offset-1 bg-primary rounded-full">
                                <img
                                  src={student?.imageURL}
                                  className="w-8 h-8 rounded-full"
                                  alt="profile"
                                />
                              </div>
                            ) : (
                              <div className="w-8 h-8 hidden lg:flex bg-primary rounded-full  items-center justify-center ring ring-[#91f2dc] ring-offset-base-100 ring-offset-1">
                                <h2 className="text-sm uppercase font-bold text-white text-center ">
                                  {student?.firstName?.slice(0, 1)}
                                  {student?.lastName?.slice(0, 1)}
                                </h2>
                              </div>
                            )}
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-bold text-lg text-purple-700">{student.roleNo}</span>
                                <div className="font-bold text-xs lg:text-sm">
                                  {student.firstName} {student.lastName}
                                </div>
                              </div>
                              {/* <div className="text-xs text-gray-500">
                                {student.class}
                              </div> */}
                              {existingResult && (
                                <div className="text-xs text-green-600 font-semibold mt-1">
                                  ✓ Result Submitted
                                </div>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Writing Input */}
                        {currentSub?.wMax && (
                          <td className="text-center">
                            {isEditModeActive ? (
                              <div>
                                <input
                                  type="number"
                                  max={currentSub.wMax}
                                  min="0"
                                  value={studentData.writing || ''}
                                  onChange={(e) => handleInputChange(student.phoneNumber, 'writing', e.target.value)}
                                  className={`input input-bordered input-sm w-20 text-center ${
                                    !validateInput(studentData.writing, currentSub.wMax, 'Writing') 
                                      ? 'input-error' 
                                      : ''
                                  }`}
                                  placeholder={existingResult?.writing || "0"}
                                />
                                {!validateInput(studentData.writing, currentSub.wMax, 'Writing') && (
                                  <div className="text-xs text-red-500 mt-1">
                                    {getValidationError(studentData.writing, currentSub.wMax, 'Writing')}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div>
                                <span className="font-bold text-lg">
                                  {existingResult?.writing || '-'}
                                </span>
                                {existingResult?.writing && (
                                  <div className="text-xs text-gray-500">
                                    /{currentSub.wMax}
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                        )}

                        {/* MCQ Input */}
                        {currentSub?.mcq && (
                          <td className="text-center">
                            {isEditModeActive ? (
                              <div>
                                <input
                                  type="number"
                                  max={currentSub.mcq}
                                  min="0"
                                  value={studentData.mcq || ''}
                                  onChange={(e) => handleInputChange(student.phoneNumber, 'mcq', e.target.value)}
                                  className={`input input-bordered input-sm w-20 text-center ${
                                    !validateInput(studentData.mcq, currentSub.mcq, 'MCQ') 
                                      ? 'input-error' 
                                      : ''
                                  }`}
                                  placeholder={existingResult?.mcq || "0"}
                                />
                                {!validateInput(studentData.mcq, currentSub.mcq, 'MCQ') && (
                                  <div className="text-xs text-red-500 mt-1">
                                    {getValidationError(studentData.mcq, currentSub.mcq, 'MCQ')}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div>
                                <span className="font-bold text-lg">
                                  {existingResult?.mcq || '-'}
                                </span>
                                {existingResult?.mcq && (
                                  <div className="text-xs text-gray-500">
                                    /{currentSub.mcq}
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                        )}

                        {/* Practical Input */}
                        {currentSub?.practical && (
                          <td className="text-center">
                            {isEditModeActive ? (
                              <div>
                                <input
                                  type="number"
                                  max={currentSub.practical}
                                  min="0"
                                  value={studentData.practical || ''}
                                  onChange={(e) => handleInputChange(student.phoneNumber, 'practical', e.target.value)}
                                  className={`input input-bordered input-sm w-20 text-center ${
                                    !validateInput(studentData.practical, currentSub.practical, 'Practical') 
                                      ? 'input-error' 
                                      : ''
                                  }`}
                                  placeholder={existingResult?.pecticNun || "0"}
                                />
                                {!validateInput(studentData.practical, currentSub.practical, 'Practical') && (
                                  <div className="text-xs text-red-500 mt-1">
                                    {getValidationError(studentData.practical, currentSub.practical, 'Practical')}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div>
                                <span className="font-bold text-lg">
                                  {existingResult?.pecticNun || '-'}
                                </span>
                                {existingResult?.pecticNun && (
                                  <div className="text-xs text-gray-500">
                                    /{currentSub.practical}
                                  </div>
                                )}
                              </div>
                            )}
                          </td>
                        )}

                        {/* Actions */}
                        <td className="text-center">
                          {!bulkEditMode && (
                            <button
                              onClick={() => toggleEditMode(student.phoneNumber)}
                              className={`btn btn-xs ${editMode[student.phoneNumber] ? 'btn-error' : 'btn-primary'}`}
                              disabled={savingStudents.has(student.phoneNumber)}
                            >
                              {editMode[student.phoneNumber] ? 'Cancel' : 'Edit'}
                            </button>
                          )}
                          {editMode[student.phoneNumber] && !bulkEditMode && (
                            <button
                              onClick={() => saveIndividualResult(student)}
                              className={`btn btn-xs btn-success ml-1 ${savingStudents.has(student.phoneNumber) ? 'loading' : ''}`}
                              disabled={savingStudents.has(student.phoneNumber)}
                            >
                              {savingStudents.has(student.phoneNumber) ? 'Saving...' : 'Save'}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Bulk Input Controls */}
        {bulkEditMode && data.length > 0 && (
          <div className="bg-white rounded-lg mt-4 p-4">
            <h3 className="text-lg font-bold mb-3">Bulk Input Controls</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {currentSub?.wMax && (
                <div>
                  <label className="label">
                    <span className="label-text">Writing Marks (Max: {currentSub.wMax})</span>
                  </label>
                  <input
                    type="number"
                    max={currentSub.wMax}
                    min="0"
                    onChange={(e) => handleBulkInputChange('writing', e.target.value)}
                    className="input input-bordered w-full"
                    placeholder="Enter writing marks for all"
                  />
                </div>
              )}
              {currentSub?.mcq && (
                <div>
                  <label className="label">
                    <span className="label-text">MCQ Marks (Max: {currentSub.mcq})</span>
                  </label>
                  <input
                    type="number"
                    max={currentSub.mcq}
                    min="0"
                    onChange={(e) => handleBulkInputChange('mcq', e.target.value)}
                    className="input input-bordered w-full"
                    placeholder="Enter MCQ marks for all"
                  />
                </div>
              )}
              {currentSub?.practical && (
                <div>
                  <label className="label">
                    <span className="label-text">Practical Marks (Max: {currentSub.practical})</span>
                  </label>
                  <input
                    type="number"
                    max={currentSub.practical}
                    min="0"
                    onChange={(e) => handleBulkInputChange('practical', e.target.value)}
                    className="input input-bordered w-full"
                    placeholder="Enter practical marks for all"
                  />
                </div>
              )}
            </div>
            
            {/* Feedback Selection */}
            <div className="mt-4">
              <label className="label">
                <span className="label-text">Feedback</span>
              </label>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={() => handleBulkInputChange('feedback', normalFeedback)}
                  className="btn btn-xs btn-secondary"
                >
                  Normal
                </button>
                <button
                  onClick={() => handleBulkInputChange('feedback', GoodFeedback)}
                  className="btn btn-xs btn-success"
                >
                  Good
                </button>
                <button
                  onClick={() => handleBulkInputChange('feedback', badFeedback)}
                  className="btn btn-xs btn-error"
                >
                  Bad
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="w-full flex items-center justify-center mt-4">
            <ProgressBar
              height="80"
              width="80"
              ariaLabel="progress-bar-loading"
              wrapperStyle={{}}
              wrapperClass="progress-bar-wrapper"
              borderColor="#F4442E"
              barColor="#51E5FF"
            />
          </div>
        )}

        {/* No Students Message */}
        {data.length === 0 && (
          <div className="bg-white rounded-lg mt-4 p-8 text-center">
            <h3 className="text-lg font-bold text-gray-500">
              No students found for the selected class
            </h3>
            <p className="text-gray-400 mt-2">
              Please select a different class or check if students are registered
            </p>
          </div>
        )}

        {/* Exam Status Messages */}
        {!Myxm && (
          <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded mt-4">
            <strong>No Active Exam:</strong> Please start an exam first to enter results.
          </div>
        )}

        {/* Excel Upload Modal */}
        <ExcelUploadModal
          isOpen={showExcelModal}
          onClose={() => setShowExcelModal(false)}
          students={data}
          subject={subject}
          classe={fullClass}
          onDataProcessed={handleExcelDataProcessed}
        />
      </div>
    </div>
  );
};

export default BulkResultEntry;
